// Placing the ownership-group number INSIDE a parcel polygon.
//
// Leaflet's `bindTooltip(..., { direction: "center" })` anchors at the layer's
// bounding-box centre. For long/narrow or L-shaped parcels that centre falls OUTSIDE
// the outline, so the number floats off the działka. Instead we compute the parcel's
// "pole of inaccessibility" — the interior point farthest from every edge (Mapbox's
// polylabel algorithm) — which is guaranteed to sit on the parcel however thin or
// concave it is. `labelCandidates` also returns a ring of interior fallback anchors so
// the caller can shift the number aside when a map object would otherwise cover it.
//
// All functions are pure and work in any planar coordinate space (the caller feeds
// projected metres, e.g. EPSG:3857), so they are unit-testable without Leaflet.

/** A polygon ring as [x, y] pairs. */
export type Ring = [number, number][];
/** A polygon: outer ring first, then holes. */
export type Rings = Ring[];

/** Signed shoelace area of a ring (planar). Sign encodes winding; callers use |area|. */
export function ringArea(ring: Ring): number {
  let a = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    a += (ring[j][0] + ring[i][0]) * (ring[j][1] - ring[i][1]);
  }
  return a / 2;
}

/** Squared distance from (px,py) to segment (ax,ay)-(bx,by). */
function segDistSq(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  let cx = ax;
  let cy = ay;
  const dx = bx - ax;
  const dy = by - ay;
  if (dx !== 0 || dy !== 0) {
    const t = ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy);
    if (t > 1) {
      cx = bx;
      cy = by;
    } else if (t > 0) {
      cx = ax + dx * t;
      cy = ay + dy * t;
    }
  }
  const ex = px - cx;
  const ey = py - cy;
  return ex * ex + ey * ey;
}

/** Even-odd point-in-ring test. */
function inRing(x: number, y: number, ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/** True when (x,y) lies inside the polygon: inside the outer ring and outside every hole. */
export function inPolygon(x: number, y: number, rings: Rings): boolean {
  if (rings.length === 0 || !inRing(x, y, rings[0])) return false;
  for (let k = 1; k < rings.length; k++) {
    if (inRing(x, y, rings[k])) return false;
  }
  return true;
}

/** Signed distance from (x,y) to the polygon boundary: positive inside, negative outside. */
function pointToPolygonDist(x: number, y: number, rings: Rings): number {
  let inside = false;
  let minSq = Infinity;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
      const d = segDistSq(x, y, xi, yi, xj, yj);
      if (d < minSq) minSq = d;
    }
  }
  return (inside ? 1 : -1) * Math.sqrt(minSq);
}

interface Cell {
  x: number;
  y: number;
  h: number; // half the cell size
  d: number; // distance from cell centre to polygon
  max: number; // max possible distance within the cell (d + half-diagonal)
}

function makeCell(x: number, y: number, h: number, rings: Rings): Cell {
  const d = pointToPolygonDist(x, y, rings);
  return { x, y, h, d, max: d + h * Math.SQRT2 };
}

/** Area-weighted centroid of the outer ring — the algorithm's first guess. */
function centroidCell(rings: Rings): Cell {
  let area = 0;
  let x = 0;
  let y = 0;
  const ring = rings[0];
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [x0, y0] = ring[i];
    const [x1, y1] = ring[j];
    const f = x0 * y1 - x1 * y0;
    x += (x0 + x1) * f;
    y += (y0 + y1) * f;
    area += f * 3;
  }
  if (area === 0) return makeCell(ring[0][0], ring[0][1], 0, rings);
  return makeCell(x / area, y / area, 0, rings);
}

// A max-heap keyed on Cell.max — the polylabel priority queue (explore the most
// promising cell first). Small inline heap avoids pulling in a dependency.
function heapPush(heap: Cell[], c: Cell): void {
  heap.push(c);
  let i = heap.length - 1;
  while (i > 0) {
    const parent = (i - 1) >> 1;
    if (heap[parent].max >= heap[i].max) break;
    [heap[parent], heap[i]] = [heap[i], heap[parent]];
    i = parent;
  }
}

function heapPop(heap: Cell[]): Cell {
  const top = heap[0];
  const last = heap.pop() as Cell;
  if (heap.length > 0) {
    heap[0] = last;
    let i = 0;
    for (;;) {
      const l = 2 * i + 1;
      const r = 2 * i + 2;
      let m = i;
      if (l < heap.length && heap[l].max > heap[m].max) m = l;
      if (r < heap.length && heap[r].max > heap[m].max) m = r;
      if (m === i) break;
      [heap[m], heap[i]] = [heap[i], heap[m]];
      i = m;
    }
  }
  return top;
}

/** Pole of inaccessibility: the interior point farthest from the polygon boundary, plus
 *  that distance (the radius of the largest inscribed circle). `precision` is the stop
 *  tolerance in the input coordinate unit. Port of Mapbox's polylabel. */
export function polylabel(
  rings: Rings,
  precision = 1,
): { x: number; y: number; distance: number } {
  const ring = rings[0] ?? [];
  if (ring.length === 0) return { x: 0, y: 0, distance: 0 };

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of ring) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  const width = maxX - minX;
  const height = maxY - minY;
  const cellSize = Math.min(width, height);
  if (cellSize === 0) return { x: minX, y: minY, distance: 0 };

  let h = cellSize / 2;
  const heap: Cell[] = [];
  for (let x = minX; x < maxX; x += cellSize) {
    for (let y = minY; y < maxY; y += cellSize) {
      heapPush(heap, makeCell(x + h, y + h, h, rings));
    }
  }

  let best = centroidCell(rings);
  const bbox = makeCell(minX + width / 2, minY + height / 2, 0, rings);
  if (bbox.d > best.d) best = bbox;

  while (heap.length > 0) {
    const cell = heapPop(heap);
    if (cell.d > best.d) best = cell;
    // No sub-cell of this cell can beat the current best by more than `precision`.
    if (cell.max - best.d <= precision) continue;
    h = cell.h / 2;
    heapPush(heap, makeCell(cell.x - h, cell.y - h, h, rings));
    heapPush(heap, makeCell(cell.x + h, cell.y - h, h, rings));
    heapPush(heap, makeCell(cell.x - h, cell.y + h, h, rings));
    heapPush(heap, makeCell(cell.x + h, cell.y + h, h, rings));
  }
  return { x: best.x, y: best.y, distance: best.d };
}

/** Interior label anchors for a polygon: the pole of inaccessibility first (the preferred
 *  spot), then a ring of fallback anchors — points offset from the pole by fractions of the
 *  inscribed-circle radius, in eight directions, keeping only those still inside the polygon.
 *  The caller shifts to a fallback when a map object overlaps the preferred anchor. */
export function labelCandidates(rings: Rings, precision = 1): [number, number][] {
  const pole = polylabel(rings, precision);
  const out: [number, number][] = [[pole.x, pole.y]];
  const r = pole.distance;
  if (r > 0) {
    // Nearer offsets first so the label prefers small nudges over big jumps.
    for (const frac of [0.55, 0.9]) {
      for (let k = 0; k < 8; k++) {
        const a = (k * Math.PI) / 4;
        const x = pole.x + Math.cos(a) * r * frac;
        const y = pole.y + Math.sin(a) * r * frac;
        if (inPolygon(x, y, rings)) out.push([x, y]);
      }
    }
  }
  return out;
}
